import React, { ReactNode, useEffect } from 'react';

// Utilities
// Components
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import { IEntity } from '../../../../../../types/api/entities/IEntity';
import { Section } from './types/Section';
import { ComboBoxInputProps } from './hooks/useComboBoxInput';
import AbstractFormsComboBox from './AbstractFormsComboBox';
import ListItemEmpty from './ListItemEmpty/ListItemEmpty';
import useSingleSelectComboBox from './hooks/useSingleSelectComboBox';
import { Namespace } from '../../../../../../translation/namespace';
import { ListBoxModifier } from './Listbox/ListBox';
import { Modifier } from '../../../../../../lib/bemUtils/Modifier';

export type SingleSelectComboBoxProps = {
  id: string;
  name?: string;
  suggestUrl: string;
  hiddenInputName: string;
  labelTitle: string;
  required: boolean;
  helpText?: string;
  modifierInput?: string;
  modifierListBox?: Modifier<ListBoxModifier>;
  validationMessage?: string;
};

export type GenericSingleSelectComboBoxProps<TResult extends IEntity, TResponse> = {
  inputValueSelector: (item: TResult) => string;
  responseMapper: (response: TResponse) => Section<TResult>[];
  children: (item: TResult) => ReactNode;
  onChange?: (item?: TResult) => void;
};

function getValidationMessage<T>(
  selectedValue: T | undefined,
  required: boolean,
  isValidInputValue: boolean,
  t: TFunction,
  validationMessage: string | undefined
) {
  if (selectedValue) {
    return '';
  }

  if (required && !isValidInputValue) {
    return t(`${Namespace.phrases}:validation.minLength`, { minLength: 2 });
  }

  return validationMessage;
}

export default function SingleSelectComboBox<T extends IEntity, TResponse>({
  children,
  responseMapper,
  inputValueSelector,
  onChange,
  validationMessage,
  ...props
}: SingleSelectComboBoxProps & Omit<ComboBoxInputProps, 'onInput'> & GenericSingleSelectComboBoxProps<T, TResponse>) {
  const { t } = useTranslation();
  const { getComboBoxProps, selectedValue, isValidInputValue } = useSingleSelectComboBox(responseMapper, props);

  // propagate onChange when selectedValue changes:
  useEffect(() => {
    if (onChange) {
      onChange(selectedValue);
    }
  }, [onChange, selectedValue]);

  const inputValue = selectedValue && inputValueSelector(selectedValue);

  return (
    <>
      <AbstractFormsComboBox
        id={props.id}
        name={props.name}
        labelHelpText={props.helpText}
        labelTitle={props.labelTitle}
        modifierInput={props.modifierInput}
        modifierListBox={props.modifierListBox}
        required={props.required}
        ariaAutoComplete="list"
        validationMessage={getValidationMessage<T>(
          selectedValue,
          props.required,
          isValidInputValue,
          t,
          validationMessage
        )}
        {...getComboBoxProps(inputValue)}
      >
        {item => (item ? children(item) : <ListItemEmpty />)}
      </AbstractFormsComboBox>
      {selectedValue ? <input type="hidden" value={selectedValue.id} name={props.hiddenInputName} /> : null}
    </>
  );
}
